<template>
  <div class="menu-tree">
    <div
      v-for="(item, index) in items"
      :key="item.id"
      class="menu-tree__block"
      :class="{ 'menu-tree__block--root': depth === 0 }"
    >
      <div
        class="menu-tree__row"
        :class="{ 'menu-tree__row--dragging': handlers.isDragging(parent, index) }"
        :style="rowStyle"
        draggable="true"
        @dragstart="handlers.dragStart($event, parent, index)"
        @dragend="handlers.dragEnd()"
        @dragover.prevent="handlers.dragOver($event, parent, index)"
        @drop="handlers.drop(parent, index)"
      >
        <span class="material-symbols-outlined menu-tree__drag-handle" aria-hidden="true">drag_indicator</span>
        <button
          v-if="item.isDropdown"
          type="button"
          class="menu-tree__expand"
          :aria-expanded="String(expandedIds.includes(item.id))"
          @click="handlers.toggleExpand(item.id)"
        >
          <span class="material-symbols-outlined menu-tree__expand-icon">{{ expandedIds.includes(item.id) ? 'expand_more' : 'chevron_right' }}</span>
        </button>
        <span v-else class="menu-tree__expand-placeholder" />
        <div class="menu-tree__label-wrap">
          <span class="menu-tree__label">{{ item.label }}</span>
          <span class="menu-tree__path">{{ item.path }}</span>
        </div>
        <div
          class="menu-tree__row-actions"
          @mousedown.stop
          @click.stop
        >
          <button
            v-if="handlers.canEdit(item)"
            type="button"
            class="menu-tree__row-action"
            aria-label="Edit"
            @click="handlers.edit(item)"
          >
            <span class="material-symbols-outlined menu-tree__row-action-icon" aria-hidden="true">edit</span>
          </button>
          <button
            v-if="handlers.canDelete(item)"
            type="button"
            class="menu-tree__row-action menu-tree__row-action--danger"
            aria-label="Delete"
            @click="handlers.remove(parent, index)"
          >
            <span class="material-symbols-outlined menu-tree__row-action-icon" aria-hidden="true">delete</span>
          </button>
        </div>
      </div>

      <template v-if="item.isDropdown && expandedIds.includes(item.id)">
        <menu-tree
          :items="item.children || []"
          :parent="item"
          :depth="depth + 1"
          :expanded-ids="expandedIds"
          :handlers="handlers"
        />
        <div class="menu-tree__add-sub" :style="addSubStyle">
          <button type="button" class="menu-tree__add-sub-link" @click="handlers.addSub(item)">
            + Add Sub-item
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
/**
 * Renders a menu branch and recurses into every dropdown child, so the tree
 * supports unlimited nesting (main item -> sub item -> sub sub item -> ...).
 * Behaviour is supplied by the `handlers` object rather than events so the
 * callbacks do not have to be re-emitted through every level of recursion.
 */
const INDENT_REM = 1.5

export default {
  name: 'MenuTree',

  props: {
    items: {
      type: Array,
      default: () => []
    },

    // The item these `items` belong to. `null` means the top-level list.
    parent: {
      type: Object,
      default: null
    },

    depth: {
      type: Number,
      default: 0
    },

    expandedIds: {
      type: Array,
      default: () => []
    },

    handlers: {
      type: Object,
      required: true
    }
  },

  computed: {
    rowStyle () {
      return { paddingLeft: `${INDENT_REM * (this.depth + 1)}rem` }
    },

    addSubStyle () {
      return { paddingLeft: `${INDENT_REM * (this.depth + 2)}rem` }
    }
  }
}
</script>

<style lang="scss" scoped>
.menu-tree {
  font-family: 'Manrope', sans-serif;
}

.menu-tree__block--root {
  border-bottom: 1px solid #f1f5f9;
  &:last-child {
    border-bottom: none;
  }
}

.menu-tree__row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  min-height: 3.5rem;
  cursor: grab;
  transition: background 0.15s;
  &:hover {
    background: rgba(248, 250, 252, 0.5);
  }
  &:active {
    cursor: grabbing;
  }
  &--dragging {
    opacity: 0.5;
    background: #f8fafc;
  }
}

.menu-tree__drag-handle {
  font-size: 1.25rem;
  color: #94a3b8;
  cursor: grab;
  user-select: none;
  flex-shrink: 0;
  &:active {
    cursor: grabbing;
  }
}

.menu-tree__expand {
  padding: 0.25rem;
  border: none;
  background: none;
  cursor: pointer;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 0.375rem;
  &:hover {
    color: #0f172a;
    background: rgba(226, 232, 240, 0.5);
  }
}

.menu-tree__expand-icon {
  font-size: 1.25rem;
}

.menu-tree__expand-placeholder {
  width: 1.5rem;
  flex-shrink: 0;
}

.menu-tree__label-wrap {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  min-width: 0;
}

.menu-tree__label {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #0f172a;
}

.menu-tree__path {
  font-size: 0.8125rem;
  color: #94a3b8;
}

.menu-tree__row-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
  flex-shrink: 0;
}

.menu-tree__row-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.375rem;
  border-radius: 0.375rem;
  transition: color 0.15s, background 0.15s;
  &:hover {
    color: #0f172a;
    background: rgba(226, 232, 240, 0.5);
  }
  &--danger:hover {
    color: #dc2626;
    background: rgba(220, 38, 38, 0.08);
  }
}

.menu-tree__row-action-icon {
  font-size: 1.25rem;
}

.menu-tree__add-sub {
  padding: 0.5rem 1.5rem 1rem;
}

.menu-tree__add-sub-link {
  font-size: 0.875rem;
  font-weight: 500;
  color: #2563eb;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
}
</style>
